import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  Alert, Box, Button, ButtonGroup,
} from '@mui/material';
import GoogleOperationsMap from './GoogleOperationsMap';
import MapPopupTheme from './MapPopupTheme';
import MapErrorBoundary from './MapErrorBoundary';
import { request } from './OperationsProvider';
import { areaColor } from './areas';
import { sitePin, vehiclePin } from './mapPin';

export const located = (p) => p && p.latitude != null && p.longitude != null && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)) && Math.abs(Number(p.latitude)) <= 90 && Math.abs(Number(p.longitude)) <= 180 && p.valid !== false;
// OpenStreetMap's tile servers are run by volunteers and their usage policy excludes applications
// like this one. They blocked us, and every street tile came back as a "403 Access blocked" image.
// Both basemaps now come from the Esri service the satellite view already used, so no new host has
// to be admitted to the Content-Security-Policy. This is still somebody else's free service: before
// relying on it commercially, move to a keyed provider — Google is already wired here, and a
// MapTiler or Stadia key would suit the raster map.
const basemaps = {
  street: {
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri — Source: Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, Esri Japan, METI, © OpenStreetMap contributors, and the GIS User Community',
  },
  satellite: {
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
  },
};
export const basemapStyle = (satellite) => {
  const base = satellite ? basemaps.satellite : basemaps.street;
  return { version: 8, sources: { basemap: { type: 'raster', tileSize: 256, tiles: [base.url], attribution: base.attribution } }, layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }] };
};
// An area as a GeoJSON ring. A circle is approximated, because MapLibre has no circle geometry:
// metres converted to degrees, with longitude narrowing towards the poles. Accurate enough for
// the few kilometres an area spans, and the same shape the Google map draws natively.
const CIRCLE_STEPS = 64;
const ring = (area) => {
  if (area.shape === 'POLYGON') {
    const outline = (area.points || [])
      .filter((p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lon)))
      .map((p) => [Number(p.lon), Number(p.lat)]);
    return outline.length > 2 ? [...outline, outline[0]] : [];
  }
  const lat = Number(area.latitude); const lon = Number(area.longitude); const radius = Number(area.radiusMeters);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || !(radius > 0)) return [];
  const dLat = radius / 111320; const dLon = radius / (111320 * Math.cos((lat * Math.PI) / 180));
  return Array.from({ length: CIRCLE_STEPS + 1 }, (unused, i) => {
    const angle = (i / CIRCLE_STEPS) * 2 * Math.PI;
    return [lon + dLon * Math.cos(angle), lat + dLat * Math.sin(angle)];
  });
};
// Trucks gather, and twenty labels on one gate is a smear. They are grouped by how far apart they
// appear rather than how far apart they are, so a yard separates as soon as it is worth separating,
// and stop grouping well before plant zoom. The map cannot do this for us: clustering is a property
// of a data source, and these labels are elements, which they must be because the raster style
// carries no glyphs to draw text with.
const CLUSTER_RADIUS = 58;
const CLUSTER_MAX_ZOOM = 14;
const groupByScreen = (map, located) => {
  if (map.getZoom() >= CLUSTER_MAX_ZOOM) return located.map((p) => [p]);
  const cells = new Map();
  located.forEach((p) => {
    const at = map.project([Number(p.longitude), Number(p.latitude)]);
    const key = `${Math.round(at.x / CLUSTER_RADIUS)}:${Math.round(at.y / CLUSTER_RADIUS)}`;
    cells.set(key, [...(cells.get(key) || []), p]);
  });
  return [...cells.values()];
};

const RasterOperationsMap = ({
  points = [], trail = [], areas = [], selectedId, onSelect, card = null, height = 520, follow = null, frame = null, plan = [],
}) => {
  const container = useRef(null); const mapRef = useRef(null); const markers = useRef([]); const popup = useRef(null);
  const shown = useRef(null); const silent = useRef(false);
  // Markers live between refreshes, keyed by what they represent, and are moved rather than
  // replaced: positions arrive every ten seconds, and rebuilding the set made the map blink.
  const drawn = useRef(new Map());
  const choose = useRef(onSelect); choose.current = onSelect;
  const [cardHost] = useState(() => (typeof document === 'undefined' ? null : document.createElement('div')));
  const [moved, setMoved] = useState(0);
  const [satellite, setSatellite] = useState(false); const [ready, setReady] = useState(0); const [error, setError] = useState(false);
  useEffect(() => {
    let map;
    try {
      map = new maplibregl.Map({ container: container.current, style: basemapStyle(false), center: [78.96, 20.59], zoom: 4, attributionControl: true });
      mapRef.current = map; map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
      map.on('style.load', () => setReady((n) => n + 1)); map.on('error', () => setError(true));
      // Grouping depends on the current view, so it is redone whenever the view settles.
      map.on('moveend', () => setMoved((n) => n + 1));
      const observer = new ResizeObserver(() => map.resize()); observer.observe(container.current);
      return () => { observer.disconnect(); markers.current.forEach((m) => m.remove()); map.remove(); mapRef.current = null; };
    } catch { setError(true); return undefined; }
  }, []);
  // Only on a real change of basemap. This effect also runs on mount, and calling setStyle while
  // the map is still loading the style it was constructed with leaves it with no rendered style at
  // all - tiles fetched, canvas present, nothing drawn.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    if (mapRef.current) { setError(false); mapRef.current.setStyle(basemapStyle(satellite)); }
  }, [satellite]);
  // Deferred rather than abandoned when the style is busy. Adding the areas source mutates the
  // style, so isStyleLoaded is false for a moment after load; bailing out there was permanent,
  // because nothing moves afterwards to run this again and the markers never appeared at all.
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready) return undefined;
    const grouped = (live, fleet, chosenId) => {
      const chosen = fleet.find((p) => p.id === chosenId);
      const groups = groupByScreen(live, chosen ? fleet.filter((p) => p !== chosen) : fleet);
      return chosen ? [[chosen], ...groups] : groups;
    };
    const apply = () => {
      const live = mapRef.current; if (!live) return;
      // A customer site is a place, not a truck. It was being fed through the same clustering, so a
      // gate standing next to one vehicle announced "2 vehicles here" - and the site was drawn as
      // another pill in the fleet. Places are drawn separately and never absorbed into a count.
      const sites = points.filter((p) => p.kind === 'site' && located(p));
      const fleet = points.filter((p) => p.kind !== 'site' && located(p));
      // What each marker stands for. A group keeps its key while it holds the same trucks, so a
      // fleet creeping across the yard keeps its markers and only moves them.
      const wanted = new Map();
      sites.forEach((site) => wanted.set(`site:${site.id}:${site.latitude},${site.longitude}`, { site }));
      // The chosen truck is drawn on its own even where its neighbours are grouped: a name
      // clicked in the list has to appear on the map, not become part of a number.
      grouped(map, fleet, selectedId).forEach((group) => {
        const key = group.length > 1
          ? `group:${group.map((p) => p.id).sort().join(',')}`
          : `vehicle:${group[0].id}${group[0].id === selectedId ? ':chosen' : ''}`;
        wanted.set(key, { group });
      });

      wanted.forEach(({ site, group }, key) => {
        const lead = site || group[0];
        let marker = drawn.current.get(key);
        if (!marker) {
          const single = !site && group.length === 1;
          const element = site ? sitePin(site)
            : (single ? vehiclePin(lead, lead.id === selectedId) : document.createElement('button'));
          if (!site && !single) {
            element.type = 'button';
            if (group.length > 1) {
              element.setAttribute('aria-label', `${group.length} vehicles here, zoom in to separate them`);
              Object.assign(element.style, {
                background: '#123247', color: 'white', border: '2px solid white', borderRadius: '50%', width: '38px', height: '38px', cursor: 'pointer', boxShadow: '0 2px 8px #0005', font: '600 13px Roboto, sans-serif',
              });
              // Opening a group is a zoom, not a selection: there is no single truck to select yet.
              element.addEventListener('click', () => {
                const live = mapRef.current; if (!live) return;
                const at = drawn.current.get(key)?.getLngLat();
                if (at) live.easeTo({ center: at, zoom: Math.min(live.getZoom() + 2.5, 17), duration: 400 });
              });
            }
          }
          // Positioned before it is added: maplibre reads the coordinate as it attaches.
          marker = new maplibregl.Marker({ element, anchor: site ? 'bottom' : 'center' })
            .setLngLat([Number(lead.longitude), Number(lead.latitude)]).addTo(map);
          drawn.current.set(key, marker);
        }
        marker.setLngLat([Number(lead.longitude), Number(lead.latitude)]);
        if (!site && group.length > 1) {
          const element = marker.getElement();
          if (element.textContent !== String(group.length)) element.textContent = String(group.length);
        }
        if (!site && group.length === 1) {
          // Markers are stacked in the order the map drew them, which has nothing to do with which
          // one somebody has just picked. The chosen truck is lifted clear of that order.
          marker.getElement().parentElement.style.zIndex = lead.id === selectedId ? '5' : '';
        }
      });
      [...drawn.current.keys()].filter((key) => !wanted.has(key)).forEach((key) => {
        drawn.current.get(key).remove(); drawn.current.delete(key);
      });
      markers.current = [...drawn.current.values()];
      const coordinates = trail.filter(located).map((p) => [Number(p.longitude), Number(p.latitude)]);
      const geo = { type: 'FeatureCollection', features: coordinates.length > 1 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates } }] : [] };
      if (map.getSource('recorded-trail')) map.getSource('recorded-trail').setData(geo);
      else { map.addSource('recorded-trail', { type: 'geojson', data: geo }); map.addLayer({ id: 'recorded-trail', type: 'line', source: 'recorded-trail', paint: { 'line-color': '#1684ff', 'line-width': 4, 'line-opacity': 0.85 } }); }
      // The stages this load was meant to pass through, in order, drawn under what was recorded.
      //
      // Inside a plant there is no road geometry to follow and none would help: a truck crosses its
      // own yard. What there is, is a route - gate, weighbridge, bay, gate out - and the useful
      // question is whether the lorry went that way. Dashed and grey so it reads as the intention
      // rather than the record, and beneath the blue so the blue always wins where they overlap.
      const intended = (plan || []).filter(located).map((c) => [Number(c.longitude), Number(c.latitude)]);
      const planGeo = { type: 'Feature', geometry: { type: 'LineString', coordinates: intended } };
      if (map.getSource('planned-stages')) map.getSource('planned-stages').setData(planGeo);
      else if (intended.length > 1) {
        map.addSource('planned-stages', { type: 'geojson', data: planGeo });
        map.addLayer({
          id: 'planned-stages',
          type: 'line',
          source: 'planned-stages',
          paint: { 'line-color': '#64748b', 'line-width': 3, 'line-opacity': 0.75, 'line-dasharray': [2, 2] },
        }, map.getLayer('recorded-trail') ? 'recorded-trail' : undefined);
      }
    };
    if (map.isStyleLoaded()) { apply(); return undefined; }
    map.once('idle', apply);
    return () => map.off('idle', apply);
  }, [points, trail, plan, selectedId, onSelect, ready, moved]);
  // The card belongs on the truck it describes. It is a popup rather than a corner panel so that
  // panning keeps it attached to the vehicle rather than to the screen.
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready || !cardHost) return undefined;
    const chosen = points.filter(located).find((p) => p.id === selectedId);
    // Taking the card down on purpose must not look like the user dismissing it, or replacing one
    // card with another would clear the selection that had just been made.
    const close = () => { silent.current = true; popup.current?.remove(); popup.current = null; silent.current = false; };
    if (!card || !chosen) { close(); shown.current = null; return undefined; }
    const at = [Number(chosen.longitude), Number(chosen.latitude)];
    if (shown.current !== selectedId || !popup.current) {
      close();
      // Which side of the marker it sits on is decided here rather than left to be measured: the
      // card is rendered into the popup after it opens, so the map would measure an empty box and
      // put a tall card off the bottom, where the map's own container clips it away. The test is
      // whether the card fits below, not which half of the map the marker is in.
      // Always above the marker. Choosing a side by measurement proved unreliable, because the card
      // is rendered in after the popup opens and selecting a vehicle also recentres the map, so the
      // geometry the decision was made on is out of date by the time it matters. Selection centres
      // the marker, and the card is bounded to fit in the half above it.
      popup.current = new maplibregl.Popup({
        closeButton: true, closeOnClick: false, maxWidth: '320px', offset: 18, anchor: 'bottom',
      });
      popup.current.on('close', () => { if (!silent.current) onSelect?.(null); });
      popup.current.setDOMContent(cardHost).setLngLat(at).addTo(map);
      shown.current = selectedId;
    } else {
      popup.current.setLngLat(at);
    }
    return undefined;
  }, [points, selectedId, card, ready, onSelect, cardHost]);
  // Drawn beneath the vehicles and the trail, and never interactive, so an area cannot swallow a
  // click meant for a marker. Re-added whenever the style reloads, since setStyle drops sources.
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready) return undefined;
    // Not gated on isStyleLoaded: the effect above adds its own source and layer first, which leaves
    // the style busy for the rest of the commit. Bailing there would be permanent, because this
    // effect only re-runs when the areas themselves change - so wait for the map to settle instead.
    const apply = () => {
      const features = areas
        .map((area) => ({ type: 'Feature', properties: { colour: areaColor(area) }, geometry: { type: 'Polygon', coordinates: [ring(area)] } }))
        .filter((feature) => feature.geometry.coordinates[0].length > 3);
      const data = { type: 'FeatureCollection', features };
      if (map.getSource('areas')) { map.getSource('areas').setData(data); return; }
      map.addSource('areas', { type: 'geojson', data });
      const beneath = map.getLayer('recorded-trail') ? 'recorded-trail' : undefined;
      map.addLayer({ id: 'area-fill', type: 'fill', source: 'areas', paint: { 'fill-color': ['get', 'colour'], 'fill-opacity': 0.12 } }, beneath);
      map.addLayer({ id: 'area-outline', type: 'line', source: 'areas', paint: { 'line-color': ['get', 'colour'], 'line-width': 2 } }, beneath);
    };
    if (map.isStyleLoaded()) { apply(); return undefined; }
    map.once('idle', apply);
    return () => map.off('idle', apply);
  }, [areas, ready]);
  // A map opens showing everything it has drawn.
  //
  // This framed the markers and nothing else, so a page holding one marker and a journey across a
  // district opened at full zoom on the marker, with the trail, the route and the plant's own areas
  // all off-screen - and the first thing anybody did was zoom out. Everything drawn is now inside
  // the opening view: where the trucks are, where they went, where they were meant to go, and the
  // ground the plant has drawn for itself.
  const fit = () => {
    const map = mapRef.current; if (!map) return;
    const everything = [
      ...points.filter(located).map((p) => [Number(p.longitude), Number(p.latitude)]),
      ...trail.filter(located).map((p) => [Number(p.longitude), Number(p.latitude)]),
      ...(plan || []).filter(located).map((p) => [Number(p.longitude), Number(p.latitude)]),
    ];
    // With nothing reporting, frame the areas instead of leaving the map on the whole country.
    if (!everything.length) {
      const corners = areas.flatMap(ring);
      if (!corners.length) return;
      const area = new maplibregl.LngLatBounds();
      corners.forEach((c) => area.extend(c));
      map.fitBounds(area, { padding: 70, maxZoom: 16, duration: 350 });
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    everything.forEach((c) => bounds.extend(c));
    areas.flatMap(ring).forEach((c) => bounds.extend(c));
    map.fitBounds(bounds, { padding: 70, maxZoom: 16, duration: 350 });
  };
  // Both keys are derived from content rather than from the arrays themselves. The fleet is polled
  // every thirty seconds and each refresh hands down new array instances holding the same areas, so
  // depending on `areas` re-framed the map on every poll — throwing away whatever the operator had
  // panned or zoomed to, which is the opposite of what the line below intends.
  const pointKey = `${points.filter(located).map((p) => p.id).join(',')
    // Keyed by the ends and the length of what is drawn rather than the arrays themselves: a poll
    // every thirty seconds hands down new arrays holding the same journey, and refitting on those
    // would throw away whatever the operator had panned to.
  }|${trail.length}:${trail[0]?.fixTime || ''}:${trail[trail.length - 1]?.fixTime || ''}`
    + `|${(plan || []).length}`;
  const areaKey = areas.map((a) => a.id).join(',');
  useEffect(fit, [pointKey, ready, areaKey]); // Fit when the visible fleet changes, preserving zoom during live updates.

  // A journey is framed once, by the whole of it.
  //
  // The fit above keys on which points are present, not where they are, so a replay - one marker
  // whose id never changes - was framed on its first position at full zoom and then left there
  // while the lorry drove off the edge of a map that never moved. Playing it back showed an empty
  // street for a minute and a half.
  const frameKey = (frame || []).length ? `${(frame || []).length}:${frame[0]?.fixTime || ''}:${frame[frame.length - 1]?.fixTime || ''}` : '';
  useEffect(() => {
    const map = mapRef.current; if (!map || !ready || !frameKey) return;
    const route = (frame || []).filter(located);
    if (route.length < 2) return;
    const bounds = new maplibregl.LngLatBounds();
    route.forEach((p) => bounds.extend([Number(p.longitude), Number(p.latitude)]));
    map.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 350 });
  }, [frameKey, ready]);

  // And then kept in view while it runs. Only while it is actually playing: somebody who has paused
  // to look at a junction should be able to pan away without the map dragging them back.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !located(follow)) return;
    const at = [Number(follow.longitude), Number(follow.latitude)];
    if (map.getBounds().contains(at)) return;
    map.easeTo({ center: at, duration: 600 });
  }, [follow?.latitude, follow?.longitude, ready]);
  useEffect(() => { const p = points.find((x) => x.id === selectedId); if (located(p) && mapRef.current) mapRef.current.easeTo({ center: [Number(p.longitude), Number(p.latitude)], zoom: Math.max(mapRef.current.getZoom(), 13), duration: 400 }); }, [selectedId]);
  return (
    <Box sx={{ height, position: 'relative', bgcolor: (t) => (t.palette.mode === 'dark' ? '#1B2733' : '#e5edf3'), overflow: 'hidden', borderRadius: 2 }}>
      {/* Sized explicitly rather than by inset: MapLibre's own stylesheet sets
          .maplibregl-map { position: relative } on whichever element it is handed, which beats the
          absolute positioning this relied on. The div then collapsed to zero height and the map
          rendered into a buffer nobody could see. */}
      <Box ref={container} sx={{ width: '100%', height: '100%' }} />
      {card && cardHost && createPortal(card, cardHost)}
      <ButtonGroup size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'background.paper' }}>
        <Button variant={!satellite ? 'contained' : 'outlined'} onClick={() => setSatellite(false)}>Map</Button>
        <Button variant={satellite ? 'contained' : 'outlined'} onClick={() => setSatellite(true)}>Satellite</Button>
        <Button onClick={fit}>{points.some((p) => p.kind === 'site') ? 'Fit all' : 'Fit fleet'}</Button>
      </ButtonGroup>
      {error && <Alert severity="warning" sx={{ position: 'absolute', bottom: 35, left: 12, right: 50 }}>Map imagery could not load. Vehicle and checkpoint details remain available.</Alert>}
      {!points.some(located) && !error && <Alert severity="info" sx={{ position: 'absolute', bottom: 35, left: 12, right: 50 }}>No mapped positions yet. Configure checkpoint coordinates or connect vehicle telemetry.</Alert>}
    </Box>
  );
};
const OperationsMap = (props) => {
  const { plannedTrail } = props;
  const [config, setConfig] = useState(null); const [provider, setProvider] = useState('standard'); const [notice, setNotice] = useState('');
  useEffect(() => { const controller = new AbortController(); request('/api/operations/maps', 'GET', null, controller.signal).then((c) => { setConfig(c); if (c.googleMapsApiKey) setProvider('google'); }).catch((e) => { if (e.name !== 'AbortError') setNotice('Map provider configuration is unavailable. Using the standard map.'); }); return () => controller.abort(); }, []);
  return (
    <Box>
      <ButtonGroup size="small" sx={{ mb: 1 }}>
        <Button variant={provider === 'standard' ? 'contained' : 'outlined'} onClick={() => { setProvider('standard'); setNotice(''); }}>Standard map</Button>
        <Button variant={provider === 'google' ? 'contained' : 'outlined'} onClick={() => { if (config?.googleMapsApiKey) { setProvider('google'); setNotice(''); } else setNotice('Google Maps is not configured. Ask your administrator to enable the Google Maps integration.'); }}>Google Maps</Button>
      </ButtonGroup>
      {notice && <Alert severity="info" sx={{ mb: 1 }}>{notice}</Alert>}
      {!!plannedTrail?.length && provider !== 'google' && <Alert severity="info" sx={{ mb: 1 }}>Select Google Maps to view the planned route. The standard map shows recorded GPS only.</Alert>}
      <MapPopupTheme />
      {provider === 'google' && config?.googleMapsApiKey ? <MapErrorBoundary onFallback={() => setProvider('standard')}><GoogleOperationsMap {...props} config={config} onFallback={() => setProvider('standard')} /></MapErrorBoundary> : <RasterOperationsMap {...props} />}
    </Box>
  );
};
export default OperationsMap;
